import { zodResolver } from '@hookform/resolvers/zod';
import { MailIcon, MessageSquareIcon, SendIcon, UserIcon } from 'lucide-react';
import { Suspense } from 'react';
import type { SubmitHandler, UseFormProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zCreateRequestBody } from 'sdk/zod.gen';
import { z } from 'zod';
import { useBreakpointAbove } from '~/hooks/use-breakpoints';
import { LegalContact } from '~/modules/auth/legal/legal-contact';
import { useDialoger } from '~/modules/common/dialoger/use-dialoger';
import { useFormWithDraft } from '~/modules/common/form-draft/use-draft-form';
import { InputFormField } from '~/modules/common/form-fields/input';
import { SubmitButton } from '~/modules/common/form-fields/submit-button';
import { toaster } from '~/modules/common/toaster/toaster';
import { useCreateRequestMutation } from '~/modules/requests/query';
import { Button } from '~/modules/ui/button';
import { Form } from '~/modules/ui/field';
import { useUserStore } from '~/modules/user/user-store';
import { lazyNamed } from '~/utils/lazy-named';

const ContactFormMap = lazyNamed(() => import('~/modules/common/contact-form/contact-form-map'), 'ContactFormMap');

export function ContactForm({ dialog: isDialog }: { dialog?: boolean }) {
  const { t } = useTranslation();
  const user = useUserStore((state) => state.user);
  const isMediumScreen = useBreakpointAbove('lg');
  const nameLabel = t('c:name').toLowerCase();
  const emailLabel = t('c:email').toLowerCase();
  const messageLabel = t('c:message').toLowerCase();

  const formSchema = zCreateRequestBody.extend({ name: z.string().min(2, t('error:name_required')) });

  type FormValues = z.infer<typeof formSchema>;

  const formOptions: UseFormProps<FormValues> = {
    resolver: zodResolver(formSchema),
    defaultValues: { name: user?.name ?? '', email: user?.email ?? '', message: '', type: 'contact' },
  };

  const form = useFormWithDraft<FormValues>('contact-form', { formOptions });

  const cancel = () => {
    form.reset();
  };

  const { mutate: createRequest, isPending } = useCreateRequestMutation();

  const onSubmit: SubmitHandler<FormValues> = (body) => {
    createRequest(body, {
      onSuccess: () => {
        toaster.success(t('c:message_sent.text'));

        if (isDialog) useDialoger.getState().remove();
        form.reset();
      },
      onError: () => {
        toaster.error(t('error:reported_try_later'));
      },
    });
  };

  return (
    <div className="flex w-full flex-col gap-4 md:flex-row md:gap-10">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col gap-4 md:gap-6">
          <InputFormField
            control={form.control}
            name="name"
            label={t('c:name')}
            placeholder={t('c:placeholder.your_input', { inputLabel: nameLabel })}
            icon={<UserIcon />}
            required
          />
          <InputFormField
            control={form.control}
            name="email"
            label={t('c:email')}
            type="email"
            placeholder={t('c:placeholder.your_input', { inputLabel: emailLabel })}
            icon={<MailIcon />}
            required
          />
          <InputFormField
            control={form.control}
            name="message"
            label={t('c:message')}
            type="textarea"
            placeholder={t('c:placeholder.your_input', { inputLabel: messageLabel })}
            icon={<MessageSquareIcon />}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <SubmitButton loading={isPending} icon={<SendIcon />}>
              {t('c:send')}
            </SubmitButton>
            <Button type="reset" variant="secondary" onClick={cancel} className={form.isDirty ? '' : 'invisible'}>
              {t('c:cancel')}
            </Button>
          </div>
        </form>
      </Form>
      {!isMediumScreen && <LegalContact className="my-6 w-full sm:my-0" addressOnly />}
      {isMediumScreen && (
        <div className="w-full overflow-hidden rounded-md bg-accent md:mb-12">
          <Suspense>
            <ContactFormMap />
          </Suspense>
        </div>
      )}
    </div>
  );
}
